import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import type { Preise } from '@/payload-types'
import { TypedLocale } from 'payload'

interface ZimmerPricingTableProps {
  pricingTable?: Preise['pricingTable']
  locale: TypedLocale
}

export const ZimmerPricingTable: React.FC<ZimmerPricingTableProps> = ({ pricingTable, locale }) => {
  if (!pricingTable || pricingTable.length === 0) {
    return (
      <div className="mb-8  border border-stone-200 bg-stone-50 p-4 text-center text-gray-900 shadow-sm">
        {locale === 'de'
          ? 'Noch keine Preise vorhanden. Bitte schauen Sie später noch einmal vorbei.'
          : 'No prices available yet. Please check back later.'}
      </div>
    )
  }

  // Collect all unique room types
  const roomTypesMap = new Map<string, string>()
  pricingTable.forEach((row) => {
    row.prices?.forEach((price) => {
      const roomCat = price['room-category']
      if (typeof roomCat === 'object' && roomCat !== null) {
        roomTypesMap.set(roomCat.id, roomCat.title)
      } else if (typeof roomCat === 'string') {
        roomTypesMap.set(roomCat, roomCat)
      }
    })
  })
  const roomTypes = Array.from(roomTypesMap.entries()) // [id, title]

  // Color scheme for different room types - more subtle
  const getRoomTypeColor = (index: number) => {
    const colors = [
      'bg-gray-50 border-gray-200 text-gray-700',
      'bg-slate-50 border-slate-200 text-slate-700',
      'bg-stone-50 border-stone-200 text-stone-700',
      'bg-neutral-50 border-neutral-200 text-neutral-700',
      'bg-zinc-50 border-zinc-200 text-zinc-700',
    ]
    return colors[index % colors.length]
  }

  // Sort pricingTable by dateRangeFrom ascending
  // Get today's date in YYYY-MM-DD format
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  const todayStr = `${yyyy}-${mm}-${dd}`

  // Filter out rows where dateRangeTo is less than today
  const filteredPricingTable = pricingTable.filter((row) => {
    if (!row.dateRangeTo) return true
    return row.dateRangeTo >= todayStr
  })

  // Sort filteredPricingTable by dateRangeFrom ascending
  const sortedPricingTable = [...filteredPricingTable].sort((a, b) => {
    if (!a.dateRangeFrom) return 1
    if (!b.dateRangeFrom) return -1
    return a.dateRangeFrom.localeCompare(b.dateRangeFrom)
  })

  if (sortedPricingTable.length === 0) {
    return (
      <div className="mb-8  border border-stone-200 bg-stone-50 p-4 text-center text-gray-900 shadow-sm">
        {locale === 'de'
          ? 'Keine Preise für diese Saison vorhanden.'
          : 'No prices available for this season.'}
      </div>
    )
  }

  return (
    <div className="mb-8  shadow-sm">
      {/* Desktop Table */}
      <div className="hidden overflow-x-auto sm:block">
        <Table className="w-full min-w-max text-xs sm:text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2">
                <span>{locale === 'de' ? 'Saison' : 'Season'}</span>
              </TableHead>
              <TableHead className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2">
                <span>{locale === 'de' ? 'Zeitraum (DD.MM.YYYY)' : 'Date Range (DD/MM/YYYY)'}</span>
              </TableHead>
              {roomTypes.map(([id, title], index) => (
                <TableHead
                  key={id}
                  className={`whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-gray-25'}`}
                >
                  <span
                    className={`inline-flex items-center  px-2.5 py-0.5 text-xs font-medium ${getRoomTypeColor(index)}`}
                  >
                    {title}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPricingTable.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2">
                  {row.season}
                </TableCell>
                <TableCell className="whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2">
                  {row.dateRangeFrom
                    ? new Date(row.dateRangeFrom).toLocaleDateString(
                        locale === 'de' ? 'de-DE' : 'en-GB',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        },
                      )
                    : ''}
                  {' - '}
                  {row.dateRangeTo
                    ? new Date(row.dateRangeTo).toLocaleDateString(
                        locale === 'de' ? 'de-DE' : 'en-GB',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        },
                      )
                    : ''}
                </TableCell>
                {roomTypes.map(([roomTypeId, title], index) => {
                  const priceObj = row.prices?.find((p) => {
                    const cat = p['room-category']
                    return typeof cat === 'object' && cat !== null
                      ? cat.id === roomTypeId
                      : cat === roomTypeId
                  })

                  // Alternate column colors: even index = light gray, odd index = lighter gray
                  const columnColorClass = index % 2 === 0 ? 'bg-gray-50' : 'bg-gray-25'

                  return (
                    <TableCell
                      key={roomTypeId}
                      className={`whitespace-nowrap px-2 py-1 sm:px-4 sm:py-2 ${columnColorClass}`}
                    >
                      <span className="font-medium">
                        {priceObj?.price ? `${priceObj.price} €` : '-'}
                      </span>
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {/* Mobile Vertical Cards */}
      <div className="block space-y-4 sm:hidden">
        {sortedPricingTable.map((row, i) => (
          <div key={i} className=" border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 font-semibold">
              {locale === 'de' ? 'Saison' : 'Season'}: {row.season}
            </div>
            <div className="mb-2 text-xs text-gray-600">
              {locale === 'de' ? 'Zeitraum' : 'Date Range'}:{' '}
              {row.dateRangeFrom
                ? new Date(row.dateRangeFrom).toLocaleDateString(
                    locale === 'de' ? 'de-DE' : 'en-GB',
                    {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    },
                  )
                : ''}
              {' - '}
              {row.dateRangeTo
                ? new Date(row.dateRangeTo).toLocaleDateString(
                    locale === 'de' ? 'de-DE' : 'en-GB',
                    {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    },
                  )
                : ''}
            </div>
            <div className="divide-y divide-gray-100">
              {roomTypes.map(([roomTypeId, title], index) => {
                const priceObj = row.prices?.find((p) => {
                  const cat = p['room-category']
                  return typeof cat === 'object' && cat !== null
                    ? cat.id === roomTypeId
                    : cat === roomTypeId
                })
                return (
                  <div key={roomTypeId} className="flex items-center justify-between py-2 text-sm">
                    <span className={`font-medium ${getRoomTypeColor(index)}`}>{title}</span>
                    <span className="font-semibold">
                      {priceObj?.price ? `${priceObj.price} €` : '-'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ZimmerPricingTable
